import { CheckIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import deepEqual from 'fast-deep-equal';

import { PageHeader } from 'components/PageHeader';
import { Pagination } from 'components/pagination/Pagination';
import { SearchType, SearchWithType } from 'components/search-with-type/SearchWithType';
import { Pill } from 'components/pill/Pill';
import { ITableHeader, Table } from 'components/table/Table';
import { OrderDocumentType, OrderLineStopType } from 'src/types/enums';
import { fetchCustomerPortalOrders, type CustomerPortalOrdersFilters } from './models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate, formatInputTime } from '../../../utils/date';
import { decimateAndFormatNumber } from '../../../utils/number';
import { ORDER_STATUS_COLOR, SALE_STATUS_COLOR } from '../../order/constants';
import { calculateLinesTotalExclVat } from '../../order/utils/price';
import { LinkButton } from '../../../components/button/ButtonLink';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { CustomerPortalOrderLocationCombobox, LocationComboValue } from './CustomerPortalOrderLocationCombobox';

export const CustomerPortalOrdersPage = () => {
  const { i18n } = useTranslation();

  const searchTypes: SearchType[] = [
    {
      label: i18n('customerPortal.orders.invoiceRef'),
      value: 'customerRef',
    },
    {
      label: i18n('customerPortal.orders.orderRef'),
      value: 'orderNumber',
    },
  ];

  const tableHeaders: ITableHeader[] = [
    {
      id: 'id',
      name: i18n('customerPortal.orders.orderRef'),
    },
    {
      id: 'customerRef',
      name: i18n('customerPortal.orders.invoiceRef'),
    },
    {
      id: 'loadLocation',
      name: i18n('customerPortal.orders.loadLocation'),
    },
    {
      id: 'unloadLocation',
      name: i18n('customerPortal.orders.unloadLocation'),
    },
    {
      id: 'loadDate',
      name: i18n('customerPortal.orders.loadDate'),
    },
    {
      id: 'loadTime',
      name: i18n('customerPortal.orders.loadTime'),
    },
    {
      id: 'unloadDate',
      name: i18n('customerPortal.orders.unloadDate'),
    },
    {
      id: 'unloadTime',
      name: i18n('customerPortal.orders.unloadTime'),
    },
    {
      id: 'saleAmount',
      name: i18n('customerPortal.orders.invoiceTotal'),
    },
    {
      id: 'status',
      name: i18n('customerPortal.orders.status'),
    },
    {
      id: 'saleStatus',
      name: i18n('customerPortal.orders.invoiceStatus'),
    },
    {
      id: 'cmr',
      name: i18n('customerPortal.orders.hasCMR'),
    },
  ];

  // Filter values
  const [search, setSearch] = useState({
    value: '',
    type: searchTypes[0].value,
  });
  const [unloadLocationFilter, setUnloadLocationFilter] = useState<LocationComboValue | null>(null);

  const [filters, setFilters] = useState<CustomerPortalOrdersFilters>({});
  const { customer } = useCustomerPortalCustomer();
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return fetchCustomerPortalOrders({
        customerId: customer.id,
        cursor: variables.cursor,
        take: variables.take,
        filters,
      });
    },
    [customer.id, filters],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [filters]);

  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      const filters: CustomerPortalOrdersFilters = {};

      const setSearch = (value: string, type: string) => {
        switch (type) {
          case 'orderNumber':
            filters.orderNumber = value;
            break;
          case 'customerRef':
            filters.customerRef = value;
            break;
        }
      };

      if (search.value) {
        setSearch(search.value, search.type);
      }

      if (unloadLocationFilter) {
        filters.unloadLocationCountry = unloadLocationFilter.country;
        filters.unloadLocationPostalCode = unloadLocationFilter.postalCode;
      }

      setFilters((prev) => {
        if (deepEqual(prev, filters)) {
          return prev;
        }
        return filters;
      });
    }, 100);
    return () => clearTimeout(timeoutRef);
  }, [search, unloadLocationFilter]);

  const searchLabel = searchTypes.find((t) => t.value === search.type)?.label ?? searchTypes[0].label;

  return (
    <>
      <PageHeader title={i18n('customerPortal.orders.orders')} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem={i18n('customerPortal.orders.orders')} />}
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                {i18n('customerPortal.orders.placeOrder')}
              </LinkButton>
            }
          />
        }
        filters={
          <FilterPanel>
            <FilterField label={searchLabel}>
              <SearchWithType
                value={search.value}
                onChange={(val) => setSearch((prev) => ({ ...prev, value: val }))}
                types={searchTypes}
                selectedType={search.type}
                onTypeChange={(val) => setSearch((prev) => ({ ...prev, type: val }))}
              />
            </FilterField>

            <CustomerPortalOrderLocationCombobox
              orderLineStopType={OrderLineStopType.Unload}
              labelText={i18n('customerPortal.orders.unloadLocation')}
              customerId={customer.id}
              value={unloadLocationFilter}
              onChange={(val) => {
                setUnloadLocationFilter(val);
              }}
            />
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(order) => {
            const orderStatusColor = ORDER_STATUS_COLOR[order.status] ?? 'neutral';
            const orderStatusText = i18n(`orderStatus.${order.status}`) ?? order.status;
            const saleStatusColor = SALE_STATUS_COLOR[order.saleStatus] ?? 'neutral';
            const saleStatusText = i18n(`orderSaleStatus.${order.saleStatus}`) ?? order.saleStatus;
            const loadStops = order.lines.map((v) => v.stops.filter((s) => s.type === OrderLineStopType.Load)).flat();
            const unloadStops = order.lines
              .map((v) => v.stops.filter((s) => s.type === OrderLineStopType.Unload))
              .flat();
            const loadDates = Array.from(new Set(loadStops.map((s) => formatDate(s.date)))).join(', ');
            const unloadDates = Array.from(new Set(unloadStops.map((s) => formatDate(s.date)))).join(', ');
            const loadTimes = Array.from(new Set(loadStops.map((s) => formatInputTime(s.timeStart)))).join(', ');
            const unloadTimes = Array.from(new Set(unloadStops.map((s) => formatInputTime(s.timeEnd)))).join(', ');
            const lineSales = order.lines.map((l) => l.sales).flat();
            const saleTotal = calculateLinesTotalExclVat(lineSales);

            return [
              <Link to={order.id} className="link-text whitespace-nowrap">
                {order.orderNumber ?? i18n('draft')}
              </Link>,
              order.customerRef,
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === OrderLineStopType.Load)
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === OrderLineStopType.Unload)
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              loadDates,
              loadTimes,
              unloadDates,
              unloadTimes,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(saleTotal, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <Pill color={orderStatusColor} size="sm">
                {orderStatusText}
              </Pill>,
              <Pill color={saleStatusColor} size="sm">
                {saleStatusText}
              </Pill>,
              order.documents.find((d) => d.type === OrderDocumentType.Cmr) ? (
                <CheckIcon className="w-6 h-6 text-positive-text" />
              ) : (
                <XIcon className="w-6 h-6 text-negative-text" />
              ),
            ];
          }}
        />
      </ListPage>
    </>
  );
};
